import z from "zod";

const sunlightErrorTypeValues = [
  "FAILED_TO_GET_FINANCING_DATA_FOR_SUNLIGHT_PROJECT_WHEN_CLAIMING_MILESTONE",
  "FAILED_TO_CLAIM_MILESTONE_WITH_SUNLIGHT",
  "FAILED_TO_REQUEST_DRAW_WITH_SUNLIGHT",
  "SUNLIGHT_DRAW_REQUEST_WAS_NOT_DELIVERED",
  "SUNLIGHT_DRAW_BEFORE_PROJECT_COMPLETION_IS_BLOCKED_IN_CALIFORNIA",
  "SUNLIGHT_CANCELLATION_WINDOW_OUTLASTED_THE_DRAW_DELAY",
  "SUNLIGHT_LOAN_DISBURSED_BEYOND_ITS_LOAN_AMOUNT",
  "FAILED_TO_GET_FINANCING_DATA_FOR_SUNLIGHT_PROJECT",
  "FAILED_TO_DOWNLOAD_FILE_FROM_S3_FOR_SUNLIGHT_PROJECT",
  "FAILED_TO_SEND_LOAN_DOCS",
  "FAILED_TO_SEND_IN_PERSON_LOAN_DOCS",
  "FAILED_TO_CREATE_OR_UPDATE_SUNLIGHT_ACCOUNT",
  "SUNLIGHT_ACCOUNT_FIELD_VALIDATION_ERROR",
  "SUNLIGHT_LOAN_AMOUNT_BELOW_PRODUCT_MINIMUM",
  "SUNLIGHT_ACCOUNT_CREATION_NO_PROJECT_RETURNED",
  "FAILED_TO_FINALIZE_SUNLIGHT_PROJECT",
  "SUNLIGHT_INACTIVE_PROJECT_OR_INCORRECT_APR_OR_TERM",
  "FAILED_TO_SUBMIT_CREDIT_APPLICATION",
  "FAILED_TO_FETCH_DISCLOSURES",
  "FAILED_TO_UPLOAD_STIPULATION_FILE",
  "FAILED_TO_AUTHENTICATE_WITH_SUNLIGHT",
  "FAILED_TO_DELETE_STIPULATION_FILE_FROM_S3",
  "SUNLIGHT_UNRECOGNIZED_PROJECT_STATE",
] as const;
const sunlightErrorTypeSchema = z.enum(sunlightErrorTypeValues);
const SunlightErrorType = sunlightErrorTypeSchema.enum;
type SunlightErrorType = z.infer<typeof sunlightErrorTypeSchema>;

export type SunlightErrors =
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_SEND_LOAN_DOCS;
      data: { errors: { errorMessage: string }[]; sunLightProjectId: string };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_GET_FINANCING_DATA_FOR_SUNLIGHT_PROJECT_WHEN_CLAIMING_MILESTONE;
      data: { externalId: string; loanAmount?: number; disbursedAmount?: number };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_CLAIM_MILESTONE_WITH_SUNLIGHT;
      data: {
        externalId: string;
        milestone: string;
        drawLevel: number;
        drawAmount: number;
        returnCode?: string;
        errors?: { errorMessage: string }[];
        responseData?: string;
        requestError?: string;
      };
    }
  | {
      errorType: typeof SunlightErrorType.SUNLIGHT_DRAW_REQUEST_WAS_NOT_DELIVERED;
      data: {
        externalId: string;
        drawLevel: number;
        drawAmount: number;
        returnCode?: string;
        errors?: { errorMessage: string }[];
        responseData?: string;
        requestError?: string;
      };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_REQUEST_DRAW_WITH_SUNLIGHT;
      data: {
        externalId: string;
        drawLevel: number;
        drawAmount: number;
        returnCode?: string;
        errors?: { errorMessage: string }[];
        responseData?: string;
        requestError?: string;
      };
    }
  | {
      errorType: typeof SunlightErrorType.SUNLIGHT_DRAW_BEFORE_PROJECT_COMPLETION_IS_BLOCKED_IN_CALIFORNIA;
      data: { externalId: string; drawLevel: number; drawAmount: number; stateCode: string };
    }
  | {
      errorType: typeof SunlightErrorType.SUNLIGHT_CANCELLATION_WINDOW_OUTLASTED_THE_DRAW_DELAY;
      data: { bidId: number; financingId: string; drawNotBefore?: Date };
    }
  | {
      errorType: typeof SunlightErrorType.SUNLIGHT_LOAN_DISBURSED_BEYOND_ITS_LOAN_AMOUNT;
      data: { externalId: string; milestone: string; loanAmount: number; disbursedAmount: number };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_GET_FINANCING_DATA_FOR_SUNLIGHT_PROJECT;
      data: { externalId: string; projectId?: string };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_DOWNLOAD_FILE_FROM_S3_FOR_SUNLIGHT_PROJECT;
      data: { externalId: string; fileId: string };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_CREATE_OR_UPDATE_SUNLIGHT_ACCOUNT;
      data: {
        externalId?: string | null;
        bidId?: string | number;
        errors: { errorMessage: string }[];
        returnCode: string;
      };
    }
  | {
      errorType: typeof SunlightErrorType.SUNLIGHT_ACCOUNT_FIELD_VALIDATION_ERROR;
      data: {
        externalId?: string | null;
        bidId?: string | number;
        errors: { errorMessage: string }[];
        returnCode: string;
      };
    }
  | {
      errorType: typeof SunlightErrorType.SUNLIGHT_LOAN_AMOUNT_BELOW_PRODUCT_MINIMUM;
      data: {
        externalId?: string | null;
        bidId?: string | number;
        errors: { errorMessage: string }[];
        returnCode: string;
        loanAmount: number;
      };
    }
  | {
      errorType: typeof SunlightErrorType.SUNLIGHT_ACCOUNT_CREATION_NO_PROJECT_RETURNED;
      data: { externalId?: string | null; bidId?: string | number; returnCode: string };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_FINALIZE_SUNLIGHT_PROJECT;
      data: { errors: { errorMessage: string }[]; sunLightProjectId: string };
    }
  | {
      errorType: typeof SunlightErrorType.SUNLIGHT_INACTIVE_PROJECT_OR_INCORRECT_APR_OR_TERM;
      data: { sunLightProjectId: string; errors: { errorMessage: string }[] };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_SUBMIT_CREDIT_APPLICATION;
      data: {
        bidId: number;
        sunlightProjectId?: string;
        returnCode?: string;
        errors?: { errorMessage: string }[];
      };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_FETCH_DISCLOSURES;
      data: { bidId: number; sunlightProjectId?: string };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_SEND_IN_PERSON_LOAN_DOCS;
      data: { errors: { errorMessage: string }[]; sunLightProjectId: string };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_UPLOAD_STIPULATION_FILE;
      data: {
        externalId: string;
        fileId: string;
        documentType: string;
        httpStatus?: number;
        returnCode?: string;
        message?: string;
        errors?: { errorMessage: string }[];
        rawBody?: string;
      };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_AUTHENTICATE_WITH_SUNLIGHT;
      data: {
        returnCode: string;
        errors: { errorMessage: string }[];
        httpStatus: number;
      };
    }
  | {
      errorType: typeof SunlightErrorType.FAILED_TO_DELETE_STIPULATION_FILE_FROM_S3;
      data: { fileId: string; error?: string };
    }
  | {
      errorType: typeof SunlightErrorType.SUNLIGHT_UNRECOGNIZED_PROJECT_STATE;
      data: { externalId: string; projectId: string; sunlightStage: string; sunlightProjectStatus?: string };
    };

export const sunlightErrorDetails: Record<SunlightErrorType, { sentryDescription: string }> = {
  FAILED_TO_SEND_LOAN_DOCS: { sentryDescription: "Failed to send loan docs to Sunlight" },
  FAILED_TO_GET_FINANCING_DATA_FOR_SUNLIGHT_PROJECT_WHEN_CLAIMING_MILESTONE: {
    sentryDescription: "Failed to get financing data for Sunlight project",
  },
  FAILED_TO_CLAIM_MILESTONE_WITH_SUNLIGHT: { sentryDescription: "Failed to claim milestone for Sunlight project" },
  FAILED_TO_REQUEST_DRAW_WITH_SUNLIGHT: { sentryDescription: "Failed to request a draw for Sunlight project" },
  SUNLIGHT_DRAW_REQUEST_WAS_NOT_DELIVERED: {
    sentryDescription: "A Sunlight draw request never reached the vendor",
  },
  SUNLIGHT_DRAW_BEFORE_PROJECT_COMPLETION_IS_BLOCKED_IN_CALIFORNIA: {
    sentryDescription: "A California Sunlight loan may only be drawn once the project is complete",
  },
  SUNLIGHT_CANCELLATION_WINDOW_OUTLASTED_THE_DRAW_DELAY: {
    sentryDescription: "Sunlight still refused a post-NTP draw a day after the cancellation window was due to close",
  },
  SUNLIGHT_LOAN_DISBURSED_BEYOND_ITS_LOAN_AMOUNT: {
    sentryDescription: "Sunlight has already disbursed more than the loan amount Remi expects",
  },
  FAILED_TO_GET_FINANCING_DATA_FOR_SUNLIGHT_PROJECT: {
    sentryDescription: "Failed to get financing data for Sunlight project",
  },
  FAILED_TO_DOWNLOAD_FILE_FROM_S3_FOR_SUNLIGHT_PROJECT: {
    sentryDescription: "Failed to download file from S3 for Sunlight project",
  },

  FAILED_TO_CREATE_OR_UPDATE_SUNLIGHT_ACCOUNT: {
    sentryDescription: "Failed to create or update Sunlight account",
  },
  SUNLIGHT_ACCOUNT_FIELD_VALIDATION_ERROR: {
    sentryDescription: "Sunlight rejected the account because one or more fields failed validation",
  },
  SUNLIGHT_LOAN_AMOUNT_BELOW_PRODUCT_MINIMUM: {
    sentryDescription: "Sunlight rejected the loan amount because it is below the selected product minimum",
  },
  SUNLIGHT_ACCOUNT_CREATION_NO_PROJECT_RETURNED: {
    sentryDescription: "Sunlight account creation succeeded but no project was returned in response",
  },
  FAILED_TO_FINALIZE_SUNLIGHT_PROJECT: {
    sentryDescription: "Failed to finalize Sunlight project",
  },
  SUNLIGHT_INACTIVE_PROJECT_OR_INCORRECT_APR_OR_TERM: {
    sentryDescription: "The Sunlight product selected is inactive for that state OR the APR/term are incorrect",
  },
  FAILED_TO_SUBMIT_CREDIT_APPLICATION: {
    sentryDescription: "Failed to submit credit application to Sunlight",
  },
  FAILED_TO_FETCH_DISCLOSURES: {
    sentryDescription: "Failed to fetch credit application disclosures from Sunlight",
  },
  FAILED_TO_SEND_IN_PERSON_LOAN_DOCS: {
    sentryDescription: "Failed to send in-person loan documents to Sunlight",
  },
  FAILED_TO_UPLOAD_STIPULATION_FILE: {
    sentryDescription: "Failed to upload stipulation document to Sunlight",
  },
  FAILED_TO_AUTHENTICATE_WITH_SUNLIGHT: {
    sentryDescription: "Sunlight rejected authentication request",
  },
  FAILED_TO_DELETE_STIPULATION_FILE_FROM_S3: {
    sentryDescription: "Failed to delete transient stipulation document from S3 after Sunlight forward",
  },
  SUNLIGHT_UNRECOGNIZED_PROJECT_STATE: {
    sentryDescription: "Sunlight returned a project stage and status that are both outside our known values",
  },
};
